import React, { forwardRef, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Divider,
  Stack,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";


// Slide-up transition
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// simple currency helper (USD default)
const formatMoney = (value, currency = "USD") =>
  (typeof value === "number" ? value : Number(value || 0)).toLocaleString(
    undefined,
    {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );

const Mobile = ({
  eventId,
  ticketTypes = [],
  open,
  close,
  title = "Save your spot",
  startsAt,
  qty = {},
  incQty,
  decQty,
  promo,
  setPromo,
  cartTotals,
  cartLoading,
  cartExpiresAt,
  applyPromo,
}) => {
  const navigate = useNavigate();
  const salesEnds = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d)) return null;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const eventWhen = startsAt
    ? new Date(startsAt).toLocaleString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

  const hasItems = useMemo(() => {
    if (!qty) return false;
    return Object.values(qty).some((n) => Number(n) > 0);
  }, [qty]);

  const [remainingMs, setRemainingMs] = useState(null);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const formatCountdown = (ms) => {
    if (ms == null) return null;
    const total = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  useEffect(() => {
    let timer;
    const update = () => {
      if (!cartExpiresAt) {
        setRemainingMs(null);
        return;
      }
      const diff = new Date(cartExpiresAt).getTime() - Date.now();
      setRemainingMs(diff > 0 ? diff : 0);
    };
    update();
    if (cartExpiresAt) {
      timer = setInterval(update, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [cartExpiresAt]);

  return (
    <Dialog
      fullScreen
      open={!!open}
      onClose={close}
      TransitionComponent={Transition}
    >
      <AppBar
        elevation={0}
        color="transparent"
        position="static"
        sx={{ bgcolor: "#f3f4ef", color: "inherit" }}
      >
        <Toolbar>
          <Typography
            variant="h6"
            sx={{
              flex: 1,
              pr: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>
          {eventWhen && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mr: 2, display: { xs: "none", sm: "block" } }}
            >
              {eventWhen}
            </Typography>
          )}
          <IconButton edge="end" onClick={close} aria-label="Close checkout">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Promo code */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 480 }}>
          <Stack direction="row" spacing={1}>
            <TextField
              variant="outlined"
              fullWidth
              size="small"
              placeholder="Promo Code"
              value={promo.toUpperCase()}
              onChange={(e) => setPromo && setPromo(e.target.value.toUpperCase())}
              inputProps={{ "aria-label": "Promo code" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#cfcfcf",
                    borderWidth: "1px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#9e9e9e",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#666",
                    borderWidth: "2px",
                  },
                },
              }}
            />
            <Button
              variant="outlined"
              onClick={() => applyPromo && applyPromo()}
              disabled={!applyPromo}
            >
              Apply
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Ticket cards or loading */}
      <Box sx={{ p: 2, pt: 0, pb: 10, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 600 }}>
          {open && (!ticketTypes || ticketTypes.length === 0) ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
              }}
            >
              <CircularProgress size={40} />
            </Box>
          ) : (
            (ticketTypes || []).map((t) => {
              const available = Math.max(0, Number(t.available || 0));
              const mpoNum = Number(t.maxPerOrder);
              const mpo = Number.isFinite(mpoNum) ? mpoNum : Infinity;
              const cap = Math.min(available, mpo);
              const current = qty[t.id] || 0;
              const incDisabled = current >= cap;

              const unitFees = Number(t.totalFees ?? 0);
              const unitTotal = Number.isFinite(Number(t.totalCost))
                ? Number(t.totalCost)
                : Number(t.price || 0) + unitFees;

              return (
                <Paper
                  key={t.id}
                  variant="outlined"
                  sx={{ mb: 2, borderRadius: 2 }}
                >
                  <Box
                    sx={{
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {t.name}
                    </Typography>
                    {cap <= 0 ? (
                      <Typography
                        variant="overline"
                        sx={{
                          fontWeight: 800,
                          letterSpacing: ".08em",
                          color: "text.secondary",
                          textTransform: "uppercase",
                        }}
                      >
                        Sold out
                      </Typography>
                    ) : (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => decQty && decQty(t)}
                          aria-label={`Decrease ${t.name}`}
                          disabled={current <= 0}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <Typography sx={{ minWidth: 16, textAlign: "center" }}>
                          {current}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => incQty && incQty(t)}
                          aria-label={`Increase ${t.name}`}
                          disabled={incDisabled}
                        >
                          <AddIcon />
                        </IconButton>
                      </Stack>
                    )}
                  </Box>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {t.isFree ? "Free" : formatMoney(unitTotal, t.currency)}
                      {unitFees > 0 && (
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          incl. {formatMoney(unitFees, t.currency)} fee
                        </Typography>
                      )}
                    </Typography>

                    {t.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {t.description}
                      </Typography>
                    )}

                    {t.salesEndAt && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        Sales end on {salesEnds(t.salesEndAt)}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              );
            })
          )}
        </Box>
      </Box>

      {/* Bottom summary bar */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: "#f3f4ef",
          borderTop: 1,
          borderColor: "divider",
          zIndex: (t) => t.zIndex.appBar,
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle1">
              {formatMoney(cartTotals?.total || 0)}
            </Typography>
            <IconButton
              size="small"
              aria-label="View total breakdown"
              onClick={() => setBreakdownOpen(true)}
              sx={{ ml: 0.5, p: 0.25, verticalAlign: 'super', transform: 'translateY(-2px)' }}
            >
              <InfoOutlinedIcon fontSize="inherit" />
            </IconButton>
            {cartLoading && <CircularProgress size={30} style={{marginLeft: 20}}/>}
            {!cartLoading && cartExpiresAt && (
              <Typography
                variant="body2"
                color={
                  remainingMs !== null && remainingMs <= 60000
                    ? "error"
                    : "text.secondary"
                }
                sx={{ ml: 1 }}
              >
                {remainingMs === 0
                  ? "Hold expired"
                  : `Cart expires in ${formatCountdown(remainingMs ?? 0)}`}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            disabled={cartLoading || !hasItems}
            onClick={() => {
              navigate(`/checkout/${eventId}`)
            }}
          >
            Check out
          </Button>
        </Box>
      </Box>

      {/* Compact breakdown dialog */}
      <Dialog
        open={breakdownOpen}
        onClose={() => setBreakdownOpen(false)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="order-summary-title"
      >
        <DialogTitle id="order-summary-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Cost breakdown</Typography>
          <IconButton onClick={() => setBreakdownOpen(false)} aria-label="Close summary">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body1">Subtotal</Typography>
              <Typography variant="body1">{formatMoney(cartTotals?.subtotal || 0)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body1">Fees</Typography>
              <Typography variant="body1">{formatMoney(cartTotals?.fees || 0)}</Typography>
            </Stack>
            <Divider sx={{ my: 0.5 }} />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Total</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatMoney(cartTotals?.total || 0)}</Typography>
            </Stack>
          </Stack>
        </DialogContent>
       
      </Dialog>
    </Dialog>
  );
};

export default Mobile;
